"use client";

import { Mail } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Phenolicengravingfaq() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const faqs = [{
    value: "item-1",
    question: "What file formats do you accept for engraving?",
    answer: "We prefer vector files such as .DXF, .AI, or .EPS for precision. If you only have a PDF or a physical sample, our engineering team can digitize your specifications to ensure the final output meets your exact requirements."
  }, {
    value: "item-2",
    question: "What is your standard turnaround time?",
    answer: "Standard production for most orders is 3–5 business days. For high-volume industrial schedules or complex MIL-SPEC projects, we provide a firm delivery date upon receipt of your purchase order and final approval of proofs."
  }, {
    value: "item-3",
    question: "Can you handle bulk asset tag orders?",
    answer: "Yes. We specialize in high-volume production for facility-wide identification. Whether you need five hundred valve tags or five thousand cable markers, our automated systems ensure consistent quality across every single piece."
  }, {
    value: "item-4",
    question: "What is your policy on order changes?",
    answer: "We understand that industrial projects evolve. Changes requested before production begins are accommodated without penalty. Once engraving has commenced, modifications may incur additional costs based on the progress of the job."
  }, {
    value: "item-5",
    question: "Do you offer shipping for remote sites?",
    answer: "We ship nationwide. All orders are securely packaged to prevent damage during transit. We provide tracking information for every shipment so you can monitor your critical identification components from our facility to your job site."
  }, {
    value: "item-6",
    question: "Can I request a sample for approval?",
    answer: "For large-scale or custom projects, we provide physical samples to verify material, font, and depth before we proceed with the full production run. This ensures total compliance with your project specifications."
  }];
  return <section data-section-id="3156" id="phenolicengravingfaq" className="bg-[#F5F2EC] text-foreground py-20 md:py-[120px]">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          
          <div ref={leftRef} className={`lg:col-span-4 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="lg:sticky lg:top-24">
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-primary mb-6">
                Technical Support
              </p>
              <h2 className="font-serif font-bold text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.05] text-[#0F1B2D] mb-6">
                Phenolic Engraving — Common Questions
              </h2>
              <p className="font-default text-base leading-relaxed text-muted-foreground mb-8 max-w-md">
                Answers to common technical questions regarding our industrial marking services. If your inquiry is specific, contact our engineering desk.
              </p>
              <Link to="/ContactPage" className="inline-block text-[#C9A227] hover:opacity-80 transition-opacity">
                <Button variant="ghost" className="group h-auto px-0 py-2 font-default text-sm tracking-wide text-inherit hover:bg-transparent hover:text-inherit">
                  <Mail className="size-4 stroke-[1.25] mr-2" />
                  <span>Contact our team</span>
                  <span className="ml-1 transition-transform duration-500 ease-out group-hover:translate-x-1">
                    →
                  </span>
                </Button>
              </Link>
            </div>
          </div>

          
          <div ref={rightRef} className={`lg:col-span-8 transition-all duration-1000 delay-150 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <Accordion type="single" collapsible className="w-full border-t border-border">
              {faqs.map((faq, i) => <AccordionItem data-index={i} key={faq.value} value={faq.value} className="border-b border-border last:border-b-0 border-l-2 border-l-[#C9A227] transition-colors hover:bg-[#0F1B2D]/5">
                  <AccordionTrigger className="group flex w-full items-center justify-between gap-6 py-8 px-6 text-left hover:no-underline [&>svg]:hidden">
                    <span className="font-serif font-bold text-xl md:text-[1.4rem] leading-snug text-[#0F1B2D] transition-colors duration-300">
                      {faq.question}
                    </span>
                    <span className="relative flex size-8 shrink-0 items-center justify-center text-primary">
                      <span className="absolute h-px w-4 bg-current" />
                      <span className="absolute h-4 w-px bg-current transition-transform duration-500 ease-out group-data-[state=open]:rotate-90 group-data-[state=open]:scale-0" />
                    </span>
                  </AccordionTrigger>
                  <AccordionContent role="region" className="pb-8 pr-12 px-6">
                    <p className="font-default text-base leading-[1.7] text-[#0F1B2D]">
                      {faq.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}